import { browserUtil, clientGuidService, tokenService } from '../../common'
import { User, authDao } from '../../dao/auth/auth-dao'
import { EventBusActionType, EventBusEntityType, eventBusUtil } from '../../util/auth-event-bus-util'
import { AnalyticsEvent } from '../analytics-events'
import { analyticsService } from '../analytics-service'

export type AuthMethod = 'email' | 'apple' | 'google'

/**
 * One place for "the backend accepted this sign-in", whatever the method. Which of the two
 * analytics events fires is the backend's call (`isNewUser`); the client cannot tell a first
 * sign-in from a returning one on its own. A new account also completes the registration on
 * the auth bus, which is what the GA / Meta CompleteRegistration pixels hang off.
 */
const reportAuthCompleted = (method: AuthMethod, result: { isNewUser?: boolean }): void => {
  const isNewUser = result.isNewUser === true
  analyticsService.track(isNewUser ? AnalyticsEvent.SIGNUP_COMPLETED : AnalyticsEvent.LOGIN_COMPLETED, {
    method,
    is_new_user: result.isNewUser ?? null,
  })
  if (isNewUser) {
    eventBusUtil().emit({ action: EventBusActionType.CREATE, entity: EventBusEntityType.REGISTRATION, meta: { method } })
  }
}

const reportAuthFailed = (method: AuthMethod, error: unknown): void => {
  analyticsService.track(AnalyticsEvent.AUTH_FAILED, { method, reason: error instanceof Error ? error.message : String(error) })
}

export const authService = {
  getUsers: (): Promise<User[]> => {
    return authDao.getUsers()
  },
  partialUpdateUser: (params: { user: User; data: Partial<User> }): Promise<User> => {
    const { user, data } = params
    return authDao.partialUpdateUser({ userId: user.id, data })
  },
  updateUserNameAndLegals: (params: { name: string; acceptAllLegals?: boolean }): Promise<void> => {
    return authDao.updateUserNameAndLegals(params)
  },
  requestEmailVerification: (user: User): Promise<void> => {
    const clientGuid = clientGuidService.getOrCreate()
    return authDao.requestEmailVerification({ userId: user.id, clientGuid })
  },
  _storeTokenAndRedirect: (params: { accessToken: string; refreshToken: string; redirectUrl: string }): void => {
    const { accessToken, refreshToken, redirectUrl } = params
    tokenService.store({ accessToken, refreshToken })
    browserUtil.redirect(redirectUrl)
  },
  agreeToLatestLegalAgreements: async (): Promise<void> => {
    await authDao.agreeToLatestLegalAgreements()
  },
  getMe: async (): Promise<User> => {
    return authDao.getUserMe()
  },
  /** The provider popup succeeded and the token is about to go to the backend. */
  signupStarted: (method: AuthMethod): void => {
    analyticsService.track(AnalyticsEvent.SIGNUP_STARTED, { method, source: 'auth_screen' })
  },
  signInWithApple: async (params: { identityToken: string; redirectUrl?: string }): Promise<void> => {
    try {
      const clientGuid = clientGuidService.getOrCreate()
      const result = await authDao.signInWithApple({ identityToken: params.identityToken, clientGuid })
      reportAuthCompleted('apple', result)
      const finalRedirectUrl = params.redirectUrl ?? window.location.origin
      authService._storeTokenAndRedirect({ ...result, redirectUrl: finalRedirectUrl })
    } catch (error) {
      reportAuthFailed('apple', error)
      throw error
    }
  },
  signInWithGoogle: async (params: { idToken: string; redirectUrl?: string }): Promise<void> => {
    const { idToken, redirectUrl } = params
    try {
      const clientGuid = clientGuidService.getOrCreate()
      const result = await authDao.signInWithGoogle({ idToken, clientGuid })
      reportAuthCompleted('google', result)
      const finalRedirectUrl = redirectUrl ?? window.location.origin
      authService._storeTokenAndRedirect({ ...result, redirectUrl: finalRedirectUrl })
    } catch (error) {
      reportAuthFailed('google', error)
      throw error
    }
  },
  requestPasswordlessLogin: async (params: { email: string; captchaToken?: string }): Promise<{ expireDate: number }> => {
    analyticsService.track(AnalyticsEvent.SIGNUP_STARTED, { method: 'email', source: 'auth_screen' })
    const result = await authDao.requestPasswordlessLogin(params)
    analyticsService.track(AnalyticsEvent.OTP_REQUESTED)
    // The Lead for GA / Meta: an email was handed over. Used to be emitted by nothing.
    eventBusUtil().emit({ action: EventBusActionType.CREATE, entity: EventBusEntityType.USER, meta: { email: params.email } })
    return result
  },
  verifyPasswordlessLogin: async (params: { email: string; code: string; redirectUrl?: string }): Promise<void> => {
    const { email, code, redirectUrl: initRedirectUrl } = params
    const clientGuid = clientGuidService.getOrCreate()
    const result = await authDao.verifyPasswordlessLogin({ email, code, clientGuid })
    reportAuthCompleted('email', result)
    const redirectUrl = initRedirectUrl ?? result.redirectUrl
    authService._storeTokenAndRedirect({ accessToken: result.accessToken, refreshToken: result.refreshToken, redirectUrl })
  },
  resendPasswordlessCode: async (params: { email: string }): Promise<{ expireDate: number }> => {
    const result = await authDao.resendPasswordlessCode(params)
    analyticsService.track(AnalyticsEvent.OTP_RESENT)
    return result
  },
}
